import React from "react";

import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image,
} from "@react-pdf/renderer";

import schoolLogo from "./cayeplogo.jpg";

const COLORS = {
    navy: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    orange: "#C0750D",
    green: "#4FAE22",
    sky: "#0F6DAB",
    white: "#FFFFFF",
    text: "#142536",
    muted: "#6B7280",
    border: "#E1E7EB",
    soft: "#F7F9FA",
};

const SCHOOL_NAME =
    "CAYEP LEARNING INSTITUTE";

const SCHOOL_ADDRESS =
    "Monrovia, Montserrado County, Liberia";

const styles = StyleSheet.create({
    page: {
        paddingTop: 32,
        paddingBottom: 45,
        paddingHorizontal: 36,
        backgroundColor: COLORS.white,
        fontFamily: "Helvetica",
        color: COLORS.text,
    },

    header: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: COLORS.navy,
        borderRadius: 9,
        padding: 17,
    },

    logoContainer: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: COLORS.white,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 15,
        padding: 5,
    },

    logo: {
        width: 54,
        height: 54,
        objectFit: "contain",
    },

    headerContent: {
        flex: 1,
    },

    schoolName: {
        color: COLORS.white,
        fontSize: 19,
        fontWeight: "bold",
    },

    title: {
        marginTop: 5,
        color: COLORS.gold,
        fontSize: 10,
        fontWeight: "bold",
        letterSpacing: 0.7,
    },

    address: {
        marginTop: 5,
        color: "#D6E1E9",
        fontSize: 7.5,
    },

    goldLine: {
        height: 4,
        backgroundColor: COLORS.gold,
        marginBottom: 18,
    },

    infoCard: {
        padding: 13,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 8,
        backgroundColor: COLORS.soft,
        marginBottom: 17,
    },

    infoTitle: {
        color: COLORS.navy,
        fontSize: 9,
        fontWeight: "bold",
        marginBottom: 10,
        textTransform: "uppercase",
    },

    infoGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
    },

    infoItem: {
        width: "50%",
        marginBottom: 8,
    },

    infoLabel: {
        color: COLORS.muted,
        fontSize: 6.8,
        marginBottom: 2,
        textTransform: "uppercase",
    },

    infoValue: {
        color: COLORS.text,
        fontSize: 8.5,
        fontWeight: "bold",
    },

    countBadge: {
        alignSelf: "flex-start",
        marginTop: 2,
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 9,
        backgroundColor: "#FFF4CA",
    },

    countBadgeText: {
        color: COLORS.orange,
        fontSize: 7,
        fontWeight: "bold",
    },

    table: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 7,
        overflow: "hidden",
    },

    tableHeader: {
        minHeight: 31,
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: COLORS.navy,
    },

    headerCell: {
        color: COLORS.white,
        fontSize: 6.8,
        fontWeight: "bold",
        paddingHorizontal: 6,
        textTransform: "uppercase",
    },

    number: {
        width: "6%",
        textAlign: "center",
    },

    course: {
        width: "31%",
    },

    facilitator: {
        width: "19%",
    },

    category: {
        width: "14%",
    },

    duration: {
        width: "12%",
    },

    amount: {
        width: "18%",
        textAlign: "right",
    },

    row: {
        minHeight: 42,
        flexDirection: "row",
        alignItems: "center",
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
    },

    alternate: {
        backgroundColor: "#FBFCFD",
    },

    cell: {
        color: COLORS.text,
        fontSize: 7.5,
        paddingHorizontal: 6,
    },

    courseName: {
        width: "31%",
        color: COLORS.navy,
        fontWeight: "bold",
    },

    facilitatorCell: {
        width: "19%",
    },

    categoryCell: {
        width: "14%",
    },

    durationCell: {
        width: "12%",
    },

    amountCell: {
        width: "18%",
        color: COLORS.navy,
        fontWeight: "bold",
        textAlign: "right",
    },

    numberCell: {
        width: "6%",
        textAlign: "center",
    },

    description: {
        marginTop: 3,
        color: COLORS.muted,
        fontSize: 6,
    },

    summary: {
        marginTop: 17,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 8,
        overflow: "hidden",
    },

    summaryHeader: {
        padding: 10,
        backgroundColor: COLORS.navy,
    },

    summaryHeaderText: {
        color: COLORS.white,
        fontSize: 8.5,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },

    summaryBody: {
        padding: 13,
        backgroundColor: COLORS.soft,
    },

    summaryRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 7,
    },

    summaryLabel: {
        color: COLORS.muted,
        fontSize: 8,
    },

    summaryValue: {
        color: COLORS.text,
        fontSize: 8,
        fontWeight: "bold",
    },

    totalRow: {
        marginTop: 4,
        paddingTop: 11,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    totalLabel: {
        color: COLORS.navy,
        fontSize: 10,
        fontWeight: "bold",
    },

    totalValue: {
        color: COLORS.navy,
        fontSize: 14,
        fontWeight: "bold",
    },

    notice: {
        marginTop: 15,
        padding: 11,
        borderLeftWidth: 4,
        borderLeftColor: COLORS.gold,
        backgroundColor: "#FFF9E8",
    },

    noticeTitle: {
        color: COLORS.navy,
        fontSize: 8,
        fontWeight: "bold",
        marginBottom: 4,
    },

    noticeText: {
        color: COLORS.muted,
        fontSize: 6.8,
        lineHeight: 1.45,
    },

    footer: {
        position: "absolute",
        left: 36,
        right: 36,
        bottom: 17,
        paddingTop: 7,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        flexDirection: "row",
        justifyContent: "space-between",
    },

    footerText: {
        color: COLORS.muted,
        fontSize: 6.5,
    },

    footerBrand: {
        color: COLORS.navy,
        fontSize: 6.5,
        fontWeight: "bold",
    },
});

const formatMoney = (value) => {
    const amount = Number(value || 0);

    if (!Number.isFinite(amount)) {
        return "0.00";
    }

    return new Intl.NumberFormat(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(amount);
};

const getCourseFromPlan = (item) => {
    if (!item) {
        return null;
    }

    return (
        item.course ||
        item.subject ||
        null
    );
};

const getCourseTitle = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.course_title ||
        course?.name ||
        item?.course_name ||
        item?.subject_name ||
        "Untitled Course"
    );
};

const getCoursePrice = (item) => {
    const value =
        item?.current_price ??
        getCourseFromPlan(item)?.current_price ??
        getCourseFromPlan(item)?.price ??
        item?.price ??
        item?.amount ??
        item?.course_price ??
        0;

    const amount = Number(value);

    return Number.isFinite(amount)
        ? amount
        : 0;
};

const getCourseFacilitator = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.facilitator_name ||
        course?.facilitator ||
        item?.facilitator_name ||
        item?.teacher_name ||
        "Not assigned"
    );
};

const getCourseCategory = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.category ||
        item?.category ||
        "General"
    );
};

const getCourseDuration = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.duration ||
        item?.duration ||
        item?.course_duration ||
        "—"
    );
};

const getCourseDescription = (item) => {
    const course =
        getCourseFromPlan(item);

    return (
        course?.description ||
        item?.description ||
        ""
    );
};

const plannedCoursesBillPdf = ({
    plans = [],
    selectedYear = null,
    student = null,
    academicYear = null,
}) => {
    const safePlans =
        Array.isArray(plans)
            ? plans
            : [];

    const studentInformation =
        student ||
        safePlans.find(
            (plan) =>
                plan?.student_information
        )?.student_information ||
        {};

    const total =
        safePlans.reduce(
            (sum, item) =>
                sum +
                getCoursePrice(item),
            0
        );

    const year =
        studentInformation
            ?.current_academic_year ||
        academicYear ||
        selectedYear?.name ||
        selectedYear?.year ||
        "Current Training Year";

    const studentName =
        studentInformation
            ?.student_name ||
        "Not provided";

    const studentId =
        studentInformation
            ?.admission_id ||
        "Not provided";

    const studentEmail =
        studentInformation
            ?.email ||
        "Not provided";

    const gradeClass =
        studentInformation
            ?.grade_class_name ||
        "Not provided";

    const division =
        studentInformation
            ?.division_assigned_name ||
        "Not provided";

    return (
        <Document
            title="CAYEP Planned Courses Bill Sheet"
            author={SCHOOL_NAME}
        >
            <Page
                size="A4"
                style={styles.page}
            >

                <View
                    style={styles.header}
                >

                    <View
                        style={
                            styles.logoContainer
                        }
                    >
                        <Image
                            src={schoolLogo}
                            style={
                                styles.logo
                            }
                        />
                    </View>

                    <View
                        style={
                            styles.headerContent
                        }
                    >
                        <Text
                            style={
                                styles.schoolName
                            }
                        >
                            {SCHOOL_NAME}
                        </Text>

                        <Text
                            style={
                                styles.title
                            }
                        >
                            STUDENT PLANNED
                            COURSES • TOTAL
                            BILL SHEET
                        </Text>

                        <Text
                            style={
                                styles.address
                            }
                        >
                            {SCHOOL_ADDRESS}
                        </Text>
                    </View>

                </View>

                <View
                    style={
                        styles.goldLine
                    }
                />

                <View
                    style={
                        styles.infoCard
                    }
                >

                    <Text
                        style={
                            styles.infoTitle
                        }
                    >
                        Student Information
                    </Text>

                    <View
                        style={
                            styles.infoGrid
                        }
                    >

                        <View
                            style={
                                styles.infoItem
                            }
                        >
                            <Text
                                style={
                                    styles.infoLabel
                                }
                            >
                                Student Name
                            </Text>

                            <Text
                                style={
                                    styles.infoValue
                                }
                            >
                                {studentName}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.infoItem
                            }
                        >
                            <Text
                                style={
                                    styles.infoLabel
                                }
                            >
                                Student ID
                            </Text>

                            <Text
                                style={
                                    styles.infoValue
                                }
                            >
                                {studentId}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.infoItem
                            }
                        >
                            <Text
                                style={
                                    styles.infoLabel
                                }
                            >
                                Email
                            </Text>

                            <Text
                                style={
                                    styles.infoValue
                                }
                            >
                                {studentEmail}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.infoItem
                            }
                        >
                            <Text
                                style={
                                    styles.infoLabel
                                }
                            >
                                Academic / Training Year
                            </Text>

                            <Text
                                style={
                                    styles.infoValue
                                }
                            >
                                {year}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.infoItem
                            }
                        >
                            <Text
                                style={
                                    styles.infoLabel
                                }
                            >
                                Program / Level
                            </Text>

                            <Text
                                style={
                                    styles.infoValue
                                }
                            >
                                {gradeClass}
                            </Text>
                        </View>

                        <View
                            style={
                                styles.infoItem
                            }
                        >
                            <Text
                                style={
                                    styles.infoLabel
                                }
                            >
                                Department / Faculty
                            </Text>

                            <Text
                                style={
                                    styles.infoValue
                                }
                            >
                                {division}
                            </Text>
                        </View>

                    </View>

                    <View
                        style={
                            styles.countBadge
                        }
                    >
                        <Text
                            style={
                                styles.countBadgeText
                        }
                        >
                            {safePlans.length}{" "}
                            PLANNED COURSE
                            {safePlans.length === 1
                                ? ""
                                : "S"}
                        </Text>
                    </View>

                </View>

                <View
                    style={styles.table}
                >

                    <View
                        style={
                            styles.tableHeader
                        }
                    >

                        <Text
                            style={[
                                styles.headerCell,
                                styles.number,
                            ]}
                        >
                            #
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.course,
                            ]}
                        >
                            Course
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.facilitator,
                            ]}
                        >
                            Facilitator
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.category,
                            ]}
                        >
                            Category
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.duration,
                            ]}
                        >
                            Duration
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            Amount
                        </Text>

                    </View>

                    {safePlans.map(
                        (
                            item,
                            index
                        ) => {
                            const description =
                                getCourseDescription(
                                    item
                                );

                            const price =
                                getCoursePrice(
                                    item
                                );

                            return (
                                <View
                                    key={
                                        item?.id ||
                                        item?.plan_id ||
                                        `course-${index}`
                                    }
                                    style={[
                                        styles.row,
                                        index % 2 === 1
                                            ? styles.alternate
                                            : {},
                                    ]}
                                >

                                    <Text
                                        style={[
                                            styles.cell,
                                            styles.numberCell,
                                        ]}
                                    >
                                        {index + 1}
                                    </Text>

                                    <View
                                        style={[
                                            styles.cell,
                                            styles.courseName,
                                        ]}
                                    >

                                        <Text>
                                            {
                                                getCourseTitle(
                                                    item
                                                )
                                            }
                                        </Text>

                                        {description && (
                                            <Text
                                                style={
                                                    styles.description
                                                }
                                            >
                                                {
                                                    description
                                                }
                                            </Text>
                                        )}

                                    </View>

                                    <Text
                                        style={[
                                            styles.cell,
                                            styles.facilitatorCell,
                                        ]}
                                    >
                                        {
                                            getCourseFacilitator(
                                                item
                                            )
                                        }
                                    </Text>

                                    <Text
                                        style={[
                                            styles.cell,
                                            styles.categoryCell,
                                        ]}
                                    >
                                        {
                                            getCourseCategory(
                                                item
                                            )
                                        }
                                    </Text>

                                    <Text
                                        style={[
                                            styles.cell,
                                            styles.durationCell,
                                        ]}
                                    >
                                        {
                                            getCourseDuration(
                                                item
                                            )
                                        }
                                    </Text>

                                    <Text
                                        style={[
                                            styles.cell,
                                            styles.amountCell,
                                        ]}
                                    >
                                        {price > 0
                                            ? `USD ${formatMoney(
                                                  price
                                              )}`
                                            : "FREE"}
                                    </Text>

                                </View>
                            );
                        }
                    )}

                </View>

                <View
                    style={
                        styles.summary
                    }
                >

                    <View
                        style={
                            styles.summaryHeader
                        }
                    >
                        <Text
                            style={
                                styles.summaryHeaderText
                            }
                        >
                            Bill Summary
                        </Text>
                    </View>

                    <View
                        style={
                            styles.summaryBody
                        }
                    >

                        <View
                            style={
                                styles.summaryRow
                            }
                        >
                            <Text
                                style={
                                    styles.summaryLabel
                                }
                            >
                                Planned Courses
                            </Text>

                            <Text
                                style={
                                    styles.summaryValue
                                }
                            >
                                {
                                    safePlans.length
                                }
                            </Text>
                        </View>

                        <View
                            style={
                                styles.summaryRow
                            }
                        >
                            <Text
                                style={
                                    styles.summaryLabel
                                }
                            >
                                Currency
                            </Text>

                            <Text
                                style={
                                    styles.summaryValue
                                }
                            >
                                United States Dollar (USD)
                            </Text>
                        </View>

                        <View
                            style={
                                styles.totalRow
                            }
                        >

                            <Text
                                style={
                                    styles.totalLabel
                                }
                            >
                                TOTAL PLANNED
                                COURSE BILL
                            </Text>

                            <Text
                                style={
                                    styles.totalValue
                                }
                            >
                                USD{" "}
                                {formatMoney(
                                    total
                                )}
                            </Text>

                        </View>

                    </View>

                </View>

                <View
                    style={styles.notice}
                >

                    <Text
                        style={
                            styles.noticeTitle
                        }
                    >
                        Important Notice
                    </Text>

                    <Text
                        style={
                            styles.noticeText
                        }
                    >
                        This bill sheet reflects
                        the courses currently
                        planned by the student.
                        It is provided for planning
                        and billing reference
                        purposes. It does not by
                        itself confirm payment,
                        enrollment approval, or
                        course completion.
                    </Text>

                </View>

                <View
                    style={styles.footer}
                >

                    <Text
                        style={
                            styles.footerText
                        }
                    >
                        CAYEP Student Portal
                        {" • "}
                        Planned Course Bill
                    </Text>

                    <Text
                        style={
                            styles.footerBrand
                        }
                    >
                        {SCHOOL_NAME}
                    </Text>

                </View>

            </Page>
        </Document>
    );
};

export default plannedCoursesBillPdf;